import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { formatTime, getStatusColor, getBatchTypeLabel, toISTDateStr } from '../../lib/utils';
import SessionDetailModal from '../../components/SessionDetailModal';
import BulkEditScopeModal from '../../components/BulkEditScopeModal';
import HolidaysPage from './HolidaysPage';

export default function CalendarPage() {
  const [section, setSection] = useState('schedule');

  return (
    <div className="space-y-6">
      <Tabs value={section} onValueChange={setSection} className="w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Calendar</h1>
          <TabsList data-testid="calendar-section-tabs">
            <TabsTrigger value="schedule" data-testid="calendar-tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="holidays" data-testid="calendar-tab-holidays">Public Holidays</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="schedule" className="mt-4">
          <CalendarScheduleView />
        </TabsContent>
        <TabsContent value="holidays" className="mt-4">
          <HolidaysPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CalendarScheduleView() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [editScopeSession, setEditScopeSession] = useState(null);
  // V14.5 Part 19s — instructor filter. Founder asked: "if I want to see
  // a particular instructor's schedule, how would I do it?" Backend already
  // accepts `instructor_id` on /api/calendar — we just expose the picker.
  const [instructorFilter, setInstructorFilter] = useState('all');
  const [instructorOptions, setInstructorOptions] = useState([]);

  useEffect(() => {
    // One-shot fetch of all instructors for the filter dropdown.
    api.get('/api/users', { params: { role: 'instructor', limit: 200 } })
      .then(res => {
        const list = (res.data?.users || res.data || []).map(u => ({
          id: u._id || u.id,
          name: u.name || u.email,
        }));
        list.sort((a, b) => a.name.localeCompare(b.name));
        setInstructorOptions(list);
      })
      .catch(() => setInstructorOptions([]));
  }, []);

  const getDateRange = () => {
    const d = new Date(currentDate);
    if (view === 'day') {
      return { from: toISTDateStr(d), to: toISTDateStr(d) };
    }
    if (view === 'week') {
      const day = d.getDay();
      const start = new Date(d); start.setDate(d.getDate() - day);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { from: toISTDateStr(start), to: toISTDateStr(end) };
    }
    // month
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: toISTDateStr(start), to: toISTDateStr(end) };
  };

  const refreshSessions = () => {
    const { from, to } = getDateRange();
    const params = { date_from: from, date_to: to };
    if (instructorFilter && instructorFilter !== 'all') params.instructor_id = instructorFilter;
    api.get('/api/calendar', { params })
      .then(res => setSessions(res.data.sessions));
  };

  useEffect(() => {
    const { from, to } = getDateRange();
    const params = { date_from: from, date_to: to };
    if (instructorFilter && instructorFilter !== 'all') params.instructor_id = instructorFilter;
    setLoading(true);
    api.get('/api/calendar', { params })
      .then(res => setSessions(res.data.sessions))
      .finally(() => setLoading(false));
  }, [currentDate, view, instructorFilter]);

  const navigateDate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'day') d.setDate(d.getDate() + dir);
    else if (view === 'week') d.setDate(d.getDate() + (7 * dir));
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const weekDays = useMemo(() => {
    if (view !== 'week') return [];
    const d = new Date(currentDate);
    const day = d.getDay();
    const start = new Date(d); start.setDate(d.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(start); dt.setDate(start.getDate() + i);
      return dt;
    });
  }, [currentDate, view]);

  // Robustly parse the hour (0–23) from either "HH:MM" (24h) or "H:MM AM/PM".
  const parseHour = (t) => {
    if (!t) return null;
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const ap = m[3] ? m[3].toUpperCase() : null;
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return h;
  };

  // Dynamic hour range: default 5 AM–9 PM, auto-expanded to include any session
  // that falls outside it (fixes early-morning 5 AM slots being hidden).
  const hours = useMemo(() => {
    let min = 5, max = 21;
    (sessions || []).forEach(s => {
      const h = parseHour(s.scheduled_time);
      if (h == null) return;
      if (h < min) min = h;
      if (h > max) max = h;
    });
    min = Math.max(0, min);
    max = Math.min(23, max);
    return Array.from({ length: max - min + 1 }, (_, i) => i + min);
  }, [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSessionsForDateHour = (date, hour) => {
    const dateStr = toISTDateStr(date);
    return sessions.filter(s => {
      if (s.scheduled_date !== dateStr) return false;
      return parseHour(s.scheduled_time) === hour;
    });
  };

  const headerText = () => {
    const opts = view === 'month' ? { month: 'long', year: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
    if (view === 'week' && weekDays.length === 7) {
      const s = weekDays[0].toLocaleDateString('en', { month: 'short', day: 'numeric' });
      const e = weekDays[6].toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${s} - ${e}`;
    }
    return currentDate.toLocaleDateString('en', opts);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        {/* V14.5 Part 19s — instructor filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={instructorFilter} onValueChange={setInstructorFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="calendar-instructor-filter">
              <SelectValue placeholder="All instructors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="calendar-instructor-filter-all">All instructors</SelectItem>
              {instructorOptions.map(opt => (
                <SelectItem key={opt.id} value={opt.id} data-testid={`calendar-instructor-${opt.id}`}>
                  {opt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="day">Day</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm font-medium">{headerText()}</span>
        <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}><ChevronRight className="h-4 w-4" /></Button>
      </div>

      {view === 'week' && (
        <Card className="rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-8 border-b">
              <div className="p-2 text-xs text-muted-foreground border-r" />
              {weekDays.map((d, i) => {
                const isToday = d.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`p-2 text-center border-r last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}>
                    <p className="text-xs text-muted-foreground">{d.toLocaleDateString('en', { weekday: 'short' })}</p>
                    <p className={`text-lg font-semibold ${isToday ? 'bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' : ''}`}>{d.getDate()}</p>
                  </div>
                );
              })}
            </div>
            <div className="max-h-[600px] overflow-y-auto scrollbar-thin" data-testid="schedule-week-grid">
              {hours.map(hour => (
                <div key={hour} className="grid grid-cols-8 border-b min-h-[60px]">
                  <div className="p-2 text-xs text-muted-foreground border-r flex items-start justify-end pr-3">
                    {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                  </div>
                  {weekDays.map((d, i) => {
                    const daySessions = getSessionsForDateHour(d, hour);
                    return (
                      <div key={i} className="border-r last:border-r-0 p-1 relative">
                        {daySessions.map(s => (
                          <div key={s.id}
                            onClick={() => setSelectedSession(s)}
                            className={`rounded-lg border p-1.5 text-xs mb-1 cursor-pointer hover:shadow-md transition-shadow duration-200 ${
                              s.batch_type === 'one_on_one' ? 'bg-teal-50 border-teal-200' : 'bg-orange-50 border-orange-200'
                            }`}
                            data-testid="schedule-event-chip">
                            <p className="font-medium truncate">{s.title}</p>
                            <p className="text-muted-foreground">{formatTime(s.scheduled_time)}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {view === 'month' && (
        <Card className="rounded-2xl">
          <CardContent className="pt-4">
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs text-muted-foreground font-medium py-2">{d}</div>
              ))}
              {(() => {
                const year = currentDate.getFullYear(), month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const cells = [];
                for (let i = 0; i < firstDay; i++) cells.push(<div key={`e-${i}`} />);
                for (let d = 1; d <= daysInMonth; d++) {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                  const daySessions = sessions.filter(s => s.scheduled_date === dateStr);
                  const isToday = toISTDateStr(new Date()) === dateStr;
                  cells.push(
                    <div key={d} className={`min-h-[80px] p-1 rounded-lg border ${isToday ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/40'}`}>
                      <p className="text-sm font-medium text-right pr-1">{d}</p>
                      {daySessions.slice(0, 3).map(s => (
                        <div key={s.id} className={`text-[10px] rounded px-1 py-0.5 mt-0.5 truncate ${s.batch_type === 'one_on_one' ? 'bg-teal-100 text-teal-800' : 'bg-orange-100 text-orange-800'}`}>
                          {formatTime(s.scheduled_time)} {s.title}
                        </div>
                      ))}
                      {daySessions.length > 3 && <p className="text-[10px] text-muted-foreground text-center">+{daySessions.length - 3} more</p>}
                    </div>
                  );
                }
                return cells;
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {view === 'day' && (
        <Card className="rounded-2xl">
          <CardContent className="pt-4">
            <div className="space-y-1">
              {hours.map(hour => {
                const dateStr = toISTDateStr(currentDate);
                const hourSessions = sessions.filter(s => {
                  if (s.scheduled_date !== dateStr) return false;
                  const [h] = (s.scheduled_time || '').split(':').map(Number);
                  return h === hour;
                });
                return (
                  <div key={hour} className="flex gap-3 min-h-[50px] border-b">
                    <div className="w-16 text-xs text-muted-foreground text-right pt-2 shrink-0">
                      {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                    </div>
                    <div className="flex-1 p-1">
                      {hourSessions.map(s => (
                        <div key={s.id} className="rounded-xl border p-3 mb-1 bg-white hover:shadow-sm transition-shadow duration-200">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{s.title}</p>
                            <Badge variant="outline" className={getStatusColor(s.status)}>{s.status}</Badge>
                            {s.batch_type && <Badge variant="outline" className="text-xs">{getBatchTypeLabel(s.batch_type)}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{formatTime(s.scheduled_time)} | {s.instructor_name} | {s.batch_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        open={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        onUpdate={refreshSessions}
        onEdit={(s) => setEditScopeSession(s)}
      />

      {/* Bulk Edit Scope Modal */}
      <BulkEditScopeModal
        session={editScopeSession}
        open={!!editScopeSession}
        onClose={() => setEditScopeSession(null)}
        onSelect={(scope) => {
          setEditScopeSession(null);
          navigate(`/admin/batches/${editScopeSession.batch_id}/edit-session?session=${editScopeSession.id}&scope=${scope}`);
        }}
      />
    </div>
  );
}
